import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as DeploymentService from "../../services/Deployment";

// ==================== Deployment Operations ====================

export const fetchAllDeployments = createAsyncThunk(
  "deployment/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.getAllDeployments(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDeploymentById = createAsyncThunk(
  "deployment/fetchById",
  async (deploymentId, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.getDeploymentById(deploymentId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDeploymentLogs = createAsyncThunk(
  "deployment/fetchLogs",
  async ({ deploymentId, includeDiff = true }, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.getDeploymentLogs(
        deploymentId,
        includeDiff
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelDeployment = createAsyncThunk(
  "deployment/cancel",
  async (deploymentId, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.cancelDeployment(deploymentId);
      return { ...response.data, deploymentId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const retryDeployment = createAsyncThunk(
  "deployment/retry",
  async (deploymentId, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.retryDeployment(deploymentId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Analytics ====================

export const fetchDeploymentAnalytics = createAsyncThunk(
  "deployment/fetchAnalytics",
  async ({ siteId, params }, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.getDeploymentAnalytics(
        siteId,
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDeploymentTrends = createAsyncThunk(
  "deployment/fetchTrends",
  async ({ siteId, params }, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.getDeploymentTrends(
        siteId,
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchDeploymentComparison = createAsyncThunk(
  "deployment/fetchComparison",
  async ({ siteId, params }, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.getDeploymentComparison(
        siteId,
        params
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchEnvironmentAnalytics = createAsyncThunk(
  "deployment/fetchEnvironmentAnalytics",
  async (siteId, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.getEnvironmentAnalytics(siteId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const compareDeployments = createAsyncThunk(
  "deployment/compare",
  async ({ deployment1Id, deployment2Id }, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.compareDeployments(
        deployment1Id,
        deployment2Id
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Scheduling ====================

export const scheduleDeployment = createAsyncThunk(
  "deployment/schedule",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.scheduleDeployment(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchScheduledDeployments = createAsyncThunk(
  "deployment/fetchScheduled",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.getScheduledDeployments(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateScheduledDeployment = createAsyncThunk(
  "deployment/updateScheduled",
  async ({ scheduleId, payload }, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.updateScheduledDeployment(
        scheduleId,
        payload
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const cancelScheduledDeployment = createAsyncThunk(
  "deployment/cancelScheduled",
  async (scheduleId, { rejectWithValue }) => {
    try {
      const response = await DeploymentService.cancelScheduledDeployment(
        scheduleId
      );
      return { ...response.data, scheduleId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ==================== Initial State ====================

const initialState = {
  // Deployments list
  deployments: [],
  deploymentsLoading: false,
  deploymentsError: null,
  deploymentsPagination: {
    total: 0,
    page: 1,
    limit: 20,
  },

  // Current deployment
  currentDeployment: null,
  currentDeploymentLoading: false,

  // Deployment logs
  deploymentLogs: null,
  logsLoading: false,

  // Analytics
  analytics: null,
  analyticsLoading: false,
  analyticsError: null,

  // Trends
  trends: null,
  trendsLoading: false,

  // Comparison
  comparison: null,
  comparisonLoading: false,

  // Environment analytics
  environmentAnalytics: null,
  environmentAnalyticsLoading: false,

  // Deployment comparison
  deploymentComparison: null,
  deploymentComparisonLoading: false,

  // Scheduled deployments
  scheduledDeployments: [],
  scheduledLoading: false,
  scheduledError: null,
};

// ==================== Slice ====================

const deploymentSlice = createSlice({
  name: "deployment",
  initialState,
  reducers: {
    clearCurrentDeployment: (state) => {
      state.currentDeployment = null;
      state.deploymentLogs = null;
    },
    clearAnalytics: (state) => {
      state.analytics = null;
      state.trends = null;
      state.comparison = null;
      state.environmentAnalytics = null;
    },
    clearComparison: (state) => {
      state.deploymentComparison = null;
    },
    updateDeploymentInList: (state, action) => {
      const { deploymentId, updates } = action.payload;
      const index = state.deployments.findIndex((d) => d.id === deploymentId);
      if (index !== -1) {
        state.deployments[index] = {
          ...state.deployments[index],
          ...updates,
        };
      }
      if (state.currentDeployment?.id === deploymentId) {
        state.currentDeployment = {
          ...state.currentDeployment,
          ...updates,
        };
      }
    },
    addDeploymentToList: (state, action) => {
      state.deployments.unshift(action.payload);
      state.deploymentsPagination.total += 1;
    },
  },
  extraReducers: (builder) => {
    // ==================== Fetch All Deployments ====================
    builder
      .addCase(fetchAllDeployments.pending, (state) => {
        state.deploymentsLoading = true;
        state.deploymentsError = null;
      })
      .addCase(fetchAllDeployments.fulfilled, (state, action) => {
        state.deploymentsLoading = false;
        state.deployments = action.payload.deployments || action.payload;
        state.deploymentsPagination = {
          total: action.payload.total || 0,
          page: action.payload.page || 1,
          limit: action.payload.limit || 20,
        };
      })
      .addCase(fetchAllDeployments.rejected, (state, action) => {
        state.deploymentsLoading = false;
        state.deploymentsError = action.payload;
      });

    // ==================== Fetch Deployment By ID ====================
    builder
      .addCase(fetchDeploymentById.pending, (state) => {
        state.currentDeploymentLoading = true;
      })
      .addCase(fetchDeploymentById.fulfilled, (state, action) => {
        state.currentDeploymentLoading = false;
        state.currentDeployment = action.payload;
      })
      .addCase(fetchDeploymentById.rejected, (state, action) => {
        state.currentDeploymentLoading = false;
        state.deploymentsError = action.payload;
      });

    // ==================== Fetch Deployment Logs ====================
    builder
      .addCase(fetchDeploymentLogs.pending, (state) => {
        state.logsLoading = true;
      })
      .addCase(fetchDeploymentLogs.fulfilled, (state, action) => {
        state.logsLoading = false;
        state.deploymentLogs = action.payload;
      })
      .addCase(fetchDeploymentLogs.rejected, (state, action) => {
        state.logsLoading = false;
      });

    // ==================== Cancel Deployment ====================
    builder
      .addCase(cancelDeployment.pending, (state) => {
        state.currentDeploymentLoading = true;
      })
      .addCase(cancelDeployment.fulfilled, (state, action) => {
        state.currentDeploymentLoading = false;
        const index = state.deployments.findIndex(
          (d) => d.id === action.payload.deploymentId
        );
        if (index !== -1) {
          state.deployments[index].status = "CANCELLED";
        }
        if (state.currentDeployment?.id === action.payload.deploymentId) {
          state.currentDeployment.status = "CANCELLED";
        }
      })
      .addCase(cancelDeployment.rejected, (state, action) => {
        state.currentDeploymentLoading = false;
        state.deploymentsError = action.payload;
      });

    // ==================== Retry Deployment ====================
    builder
      .addCase(retryDeployment.pending, (state) => {
        state.currentDeploymentLoading = true;
      })
      .addCase(retryDeployment.fulfilled, (state, action) => {
        state.currentDeploymentLoading = false;
        state.deployments.unshift(action.payload);
        state.currentDeployment = action.payload;
      })
      .addCase(retryDeployment.rejected, (state, action) => {
        state.currentDeploymentLoading = false;
        state.deploymentsError = action.payload;
      });

    // ==================== Analytics ====================
    builder
      .addCase(fetchDeploymentAnalytics.pending, (state) => {
        state.analyticsLoading = true;
        state.analyticsError = null;
      })
      .addCase(fetchDeploymentAnalytics.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchDeploymentAnalytics.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.analyticsError = action.payload;
      });

    builder
      .addCase(fetchDeploymentTrends.pending, (state) => {
        state.trendsLoading = true;
      })
      .addCase(fetchDeploymentTrends.fulfilled, (state, action) => {
        state.trendsLoading = false;
        state.trends = action.payload;
      })
      .addCase(fetchDeploymentTrends.rejected, (state, action) => {
        state.trendsLoading = false;
      });

    builder
      .addCase(fetchDeploymentComparison.pending, (state) => {
        state.comparisonLoading = true;
      })
      .addCase(fetchDeploymentComparison.fulfilled, (state, action) => {
        state.comparisonLoading = false;
        state.comparison = action.payload;
      })
      .addCase(fetchDeploymentComparison.rejected, (state, action) => {
        state.comparisonLoading = false;
      });

    builder
      .addCase(fetchEnvironmentAnalytics.pending, (state) => {
        state.environmentAnalyticsLoading = true;
      })
      .addCase(fetchEnvironmentAnalytics.fulfilled, (state, action) => {
        state.environmentAnalyticsLoading = false;
        state.environmentAnalytics = action.payload;
      })
      .addCase(fetchEnvironmentAnalytics.rejected, (state, action) => {
        state.environmentAnalyticsLoading = false;
      });

    builder
      .addCase(compareDeployments.pending, (state) => {
        state.deploymentComparisonLoading = true;
      })
      .addCase(compareDeployments.fulfilled, (state, action) => {
        state.deploymentComparisonLoading = false;
        state.deploymentComparison = action.payload;
      })
      .addCase(compareDeployments.rejected, (state, action) => {
        state.deploymentComparisonLoading = false;
      });

    // ==================== Scheduled Deployments ====================
    builder
      .addCase(scheduleDeployment.pending, (state) => {
        state.scheduledLoading = true;
        state.scheduledError = null;
      })
      .addCase(scheduleDeployment.fulfilled, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledDeployments.unshift(action.payload);
      })
      .addCase(scheduleDeployment.rejected, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledError = action.payload;
      });

    builder
      .addCase(fetchScheduledDeployments.pending, (state) => {
        state.scheduledLoading = true;
      })
      .addCase(fetchScheduledDeployments.fulfilled, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledDeployments = action.payload;
      })
      .addCase(fetchScheduledDeployments.rejected, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledError = action.payload;
      });

    builder
      .addCase(updateScheduledDeployment.pending, (state) => {
        state.scheduledLoading = true;
      })
      .addCase(updateScheduledDeployment.fulfilled, (state, action) => {
        state.scheduledLoading = false;
        const index = state.scheduledDeployments.findIndex(
          (d) => d.id === action.payload.id
        );
        if (index !== -1) {
          state.scheduledDeployments[index] = action.payload;
        }
      })
      .addCase(updateScheduledDeployment.rejected, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledError = action.payload;
      });

    builder
      .addCase(cancelScheduledDeployment.pending, (state) => {
        state.scheduledLoading = true;
      })
      .addCase(cancelScheduledDeployment.fulfilled, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledDeployments = state.scheduledDeployments.filter(
          (d) => d.id !== action.payload.scheduleId
        );
      })
      .addCase(cancelScheduledDeployment.rejected, (state, action) => {
        state.scheduledLoading = false;
        state.scheduledError = action.payload;
      });
  },
});

export const {
  clearCurrentDeployment,
  clearAnalytics,
  clearComparison,
  updateDeploymentInList,
  addDeploymentToList,
} = deploymentSlice.actions;

export default deploymentSlice.reducer;
